import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const normalizeCategoryList = (rawValues, limit = 20) => {
  if (!Array.isArray(rawValues)) return [];
  const output = [];
  const seen = new Set();
  rawValues.forEach((raw) => {
    const value = typeof raw === 'string' ? raw : (raw?.name || raw?.displayName || '');
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    output.push(normalized);
  });
  return output.slice(0, limit);
};

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: 'linear-gradient(180deg, #0a0a0f 0%, #0f0f18 50%, #0a0a0f 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '800px',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 60%)',
    pointerEvents: 'none'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(139, 92, 246, 0.1)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px',
  backdropFilter: 'blur(10px)'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#a78bfa',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #c4b5fd 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: `${shimmer} 3s linear infinite`
});

const CategoryGroupsWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '48px'
});

const CategoryGroup = styled(Box)(({ index }) => ({
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.15}s`,
  animationFillMode: 'backwards'
}));

const GroupHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px'
});

const GroupIcon = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(129, 140, 248, 0.1) 100%)',
  border: '1px solid rgba(139, 92, 246, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '22px',
    color: '#a78bfa'
  }
});

const GroupTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const TagsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px'
});

const BaseTag = styled(Box)({
  padding: '14px 28px',
  borderRadius: '50px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'default',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden'
});

const CategoryTag = styled(BaseTag)({
  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(129, 140, 248, 0.08) 100%)',
  border: '1px solid rgba(139, 92, 246, 0.3)',
  color: '#c4b5fd',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.2), transparent)',
    transition: 'left 0.5s ease'
  },
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(129, 140, 248, 0.2) 100%)',
    borderColor: 'rgba(139, 92, 246, 0.5)',
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 30px rgba(139, 92, 246, 0.2)',
    '&::before': {
      left: '100%'
    }
  }
});

const ServiceTag = styled(BaseTag)({
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    color: '#ffffff'
  }
});

const AttributeTag = styled(BaseTag)({
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.05) 100%)',
  border: '1px solid rgba(16, 185, 129, 0.3)',
  color: '#6ee7b7',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& svg': {
    fontSize: '16px'
  },
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.1) 100%)',
    borderColor: 'rgba(16, 185, 129, 0.5)',
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 30px rgba(16, 185, 129, 0.15)'
  }
});

const Divider = styled(Box)({
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.2), transparent)',
  margin: '8px 0'
});

const CategoryTagsSection = ({ businessInfo, categories, services, products, keywordsData }) => {
  // Use AI-generated categories if available (same as free website), otherwise fall back to GMB categories
  const aiRelatedCategories = normalizeCategoryList([
    ...(Array.isArray(businessInfo?.aiRelatedCategories) ? businessInfo.aiRelatedCategories : []),
    ...(Array.isArray(categories) ? categories : [])
  ], 12);

  const fallbackGmbCategories = normalizeCategoryList([
    businessInfo?.category,
    ...(Array.isArray(businessInfo?.gmbCategories) ? businessInfo.gmbCategories : [])
  ], 8);

  const allCategories = aiRelatedCategories.length > 0 ? aiRelatedCategories : fallbackGmbCategories;

  // Collect tags from keywords data, services, and products
  const allTags = new Set();

  if (Array.isArray(keywordsData) && keywordsData.length > 0) {
    keywordsData.forEach(keyword => {
      if (keyword?.searchTerm) {
        allTags.add(keyword.searchTerm);
      } else if (typeof keyword === 'string') {
        allTags.add(keyword);
      }
    });
  }

  if (Array.isArray(services) && services.length > 0) {
    services.forEach(service => {
      const hasRealData = service?.id || (service?.name && service.name !== 'placeholder');
      if (hasRealData) {
        if (service?.name) allTags.add(service.name);
        else if (service?.title) allTags.add(service.title);
      }
    });
  }

  if (Array.isArray(products) && products.length > 0) {
    products.forEach(product => {
      const hasRealData = product?.id || (product?.name && product.name !== 'placeholder');
      if (hasRealData) {
        if (product?.name) allTags.add(product.name);
        else if (product?.title) allTags.add(product.title);
      }
    });
  }

  const tagsArray = Array.from(allTags).slice(0, 15);

  // Collect attribute tags
  const attributeTags = [];
  if (businessInfo?.hasWifi) attributeTags.push('WiFi Available');
  if (businessInfo?.petFriendly) attributeTags.push('Pet Friendly');
  if (businessInfo?.outdoorSeating) attributeTags.push('Outdoor Seating');
  if (businessInfo?.parking) attributeTags.push('Parking Available');
  if (businessInfo?.wheelchairAccessible) attributeTags.push('Wheelchair Accessible');

  const hasCategories = allCategories.length > 0;
  const hasTags = tagsArray.length > 0;
  const hasAttributes = attributeTags.length > 0;

  if (!hasCategories && !hasTags && !hasAttributes) {
    return null;
  }

  let groupIndex = 0;

  return (
    <SectionContainer id="tags">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <LocalOfferIcon sx={{ fontSize: 16, color: '#a78bfa' }} />
            <BadgeText>Discover</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Tags & <GradientText>Categories</GradientText>
          </SectionTitle>
        </SectionHeader>

        <CategoryGroupsWrapper>
          {hasCategories && (
            <CategoryGroup index={groupIndex++}>
              <GroupHeader>
                <GroupIcon>
                  <CategoryIcon />
                </GroupIcon>
                <GroupTitle>Categories</GroupTitle>
              </GroupHeader>
              <TagsContainer>
                {allCategories.map((cat, index) => (
                  <CategoryTag key={`cat-${index}`}>{cat}</CategoryTag>
                ))}
              </TagsContainer>
            </CategoryGroup>
          )}

          {hasCategories && (hasTags || hasAttributes) && <Divider />}

          {hasTags && (
            <CategoryGroup index={groupIndex++}>
              <GroupHeader>
                <GroupIcon>
                  <LocalOfferIcon />
                </GroupIcon>
                <GroupTitle>Tags</GroupTitle>
              </GroupHeader>
              <TagsContainer>
                {tagsArray.map((tag, index) => (
                  <ServiceTag key={`tag-${index}`}>{tag}</ServiceTag>
                ))}
              </TagsContainer>
            </CategoryGroup>
          )}

          {hasTags && hasAttributes && <Divider />}

          {hasAttributes && (
            <CategoryGroup index={groupIndex++}>
              <GroupHeader>
                <GroupIcon>
                  <CheckCircleIcon />
                </GroupIcon>
                <GroupTitle>Features & Amenities</GroupTitle>
              </GroupHeader>
              <TagsContainer>
                {attributeTags.map((attr, index) => (
                  <AttributeTag key={`attr-${index}`}>
                    <CheckCircleIcon />
                    {attr}
                  </AttributeTag>
                ))}
              </TagsContainer>
            </CategoryGroup>
          )}
        </CategoryGroupsWrapper>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default CategoryTagsSection;
